import React, { useCallback, useEffect, useState } from 'react';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { axiosInstance } from '../../utils/axiosInstance';
import { Location } from '../../interfaces/interfaces';
import { Card } from '../ui';
import {
  TimelineMachine,
  TimelineSegment,
  computeTimelineSummary,
  formatDuration,
} from './statusTimelineUtils';
import './statusTimeline.scss';

type CompareRow = {
  siteId: string;
  name: string;
  loading: boolean;
  error?: string;
  availablePct?: number;
  inUsePct?: number;
  errorOfflineMs?: number;
  machinesInErrorNow?: number;
  machineCount?: number;
};

type Props = {
  locations: Location[];
  currentSiteId: string;
  /** Lookback window in ms (same as timeline preset default when not custom) */
  lookbackMs?: number;
};

const MAX_COMPARE = 3;

const StatusLocationCompare: React.FC<Props> = ({
  locations,
  currentSiteId,
  lookbackMs = 60 * 60 * 1000,
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [rows, setRows] = useState<CompareRow[]>([]);

  const candidates = locations.filter((loc) => {
    const id = loc.layout_site_id != null ? String(loc.layout_site_id) : '';
    return id && id !== currentSiteId;
  });

  const toggleSite = (siteId: string) => {
    setSelected((prev) => {
      if (prev.includes(siteId)) return prev.filter((s) => s !== siteId);
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, siteId];
    });
  };

  const runCompare = useCallback(async () => {
    if (selected.length === 0) {
      setRows([]);
      return;
    }
    const end = Date.now();
    const start = end - lookbackMs;
    const siteIds = selected.slice(0, MAX_COMPARE);

    setRows(
      siteIds.map((siteId) => {
        const loc = locations.find(
          (l) => l.layout_site_id != null && String(l.layout_site_id) === siteId
        );
        return {
          siteId,
          name: loc?.name || siteId,
          loading: true,
        };
      })
    );

    const results = await Promise.all(
      siteIds.map(async (siteId) => {
        const loc = locations.find(
          (l) => l.layout_site_id != null && String(l.layout_site_id) === siteId
        );
        const name = loc?.name || siteId;
        try {
          const { data } = await axiosInstance.post('/health/getStatusTimeline', {
            siteId,
            startDate: new Date(start).toISOString(),
            endDate: new Date(end).toISOString(),
          });
          const machines: TimelineMachine[] = data.machines || [];
          const segments: TimelineSegment[] = data.segments || [];
          const summary = computeTimelineSummary(machines, segments, start, end);
          return {
            siteId,
            name,
            loading: false,
            availablePct: summary.availablePct,
            inUsePct: summary.inUsePct,
            errorOfflineMs: summary.errorOfflineMs,
            machinesInErrorNow: summary.machinesInErrorNow,
            machineCount: summary.machineCount,
          } as CompareRow;
        } catch (err: any) {
          return {
            siteId,
            name,
            loading: false,
            error: err?.response?.data?.error || err?.message || 'Failed',
          } as CompareRow;
        }
      })
    );
    setRows(results);
  }, [selected, lookbackMs, locations]);

  useEffect(() => {
    if (!open) return;
    if (selected.length === 0) {
      setRows([]);
      return;
    }
    runCompare();
  }, [open, selected, runCompare]);

  return (
    <Card
      flush
      className="sq-status-compare"
      title={
        <button
          type="button"
          className="sq-card-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
          Compare locations
        </button>
      }
      subtitle={
        open
          ? `Pick up to ${MAX_COMPARE} other locations. Summary metrics over the last hour.`
          : undefined
      }
    >
      {open ? (
        <>
          <div className="sq-compare-picks">
            {candidates.map((loc) => {
              const siteId = String(loc.layout_site_id);
              const checked = selected.includes(siteId);
              const disabled = !checked && selected.length >= MAX_COMPARE;
              return (
                <label key={siteId} className={`sq-check${disabled ? ' is-disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleSite(siteId)}
                  />
                  {loc.name} ({loc.locationPin})
                </label>
              );
            })}
            {candidates.length === 0 && (
              <span className="sq-muted">No other locations available.</span>
            )}
          </div>

          {rows.length > 0 && (
            <div className="sq-table-wrap">
              <table className="sq-table is-stacked">
                <thead>
                  <tr>
                    <th scope="col">Location</th>
                    <th scope="col" className="num">Machines</th>
                    <th scope="col" className="num">Available</th>
                    <th scope="col" className="num">In use</th>
                    <th scope="col" className="num">Error / offline</th>
                    <th scope="col" className="num">In error now</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.siteId}>
                      <td className="cell-title sq-strong">{r.name}</td>
                      {r.loading ? (
                        <td colSpan={5} className="cell-wide">
                          Loading…
                        </td>
                      ) : r.error ? (
                        <td colSpan={5} className="cell-wide sq-value-bad">
                          {r.error}
                        </td>
                      ) : (
                        <>
                          <td className="num" data-label="Machines">{r.machineCount}</td>
                          <td className="num" data-label="Available">{r.availablePct}%</td>
                          <td className="num" data-label="In use">{r.inUsePct}%</td>
                          <td className="num" data-label="Error / offline">
                            {formatDuration(r.errorOfflineMs || 0)}
                          </td>
                          <td className="num" data-label="In error now">{r.machinesInErrorNow}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </Card>
  );
};

export default StatusLocationCompare;
